import {
  SubscriptionResponse,
  SubscriptionSource,
  SubscriptionStatus,
} from '@sworkit/api';
import { TypeKeys, ActionTypes } from '../actions/index';
import { translate, formatDate } from '../global/translate';
//todo: simplify with updated user doc
const getInitialState = (): SubscriptionState => {
  return {
    subscriptionLoaded: false,
    label: null,
    description: null,
    sourceTranslation: null,
    status: null,
    expiration: null,
    source: null,
    hasAccess: false,
    freeWorkoutLimit: 1,
    freeWorkoutCount: 0,
    freeWorkoutsLeft: 0,
    shouldShowPaywall: false,
    isFreeWorkout: false,
    showExpiringSoon: false,
    isAuthenticated: false,
    canPlayWorkout: false,
  };
};
const exemptFromExpiring: SubscriptionSource[] = [
  'anderzorg',
  'doctors-of-bc',
  'mecklenburg-county',
  'flvs',
  'menzis',
  'solera',
  'vinkvink',
  'virgin-pulse',
  'personify-health'
];
const subscription = (
  state = getInitialState(),
  action: ActionTypes
): SubscriptionState => {
  switch (action.type) {
    case TypeKeys.CHECKING_ACTIVE_SUBSCRIPTION: {
      return {
        ...state,
        subscriptionLoaded: false,
      };
    }
    case TypeKeys.SIGN_IN: {
      return {
        ...state,
        isAuthenticated: true,
      };
    }
    case TypeKeys.SET_SUBSCRIPTION: {
      const subscriptionResponse = action.subscription;
      const sub = updateSubscriptionState(subscriptionResponse, state);
      return {
        ...state,
        ...sub,
      };
    }
    case TypeKeys.SET_PAYMENT_SUCCESS: {
      const label = action.purchasedProduct?.title || state.label;
      return {
        ...state,
        label,
        status: 'active',
        hasAccess: true,
      };
    }
    case TypeKeys.SET_SUBSCRIPTION_CANCELLED: {
      return {
        ...state,
        description: getSubscriptionDescription(
          state.expiration,
          'expiring',
          state.source
        ),
        status: 'expiring',
      };
    }
    case TypeKeys.SET_USER_DOC: {
      let freeWorkoutLimit = 0;
      if (
        action.userDoc &&
        action.userDoc.account &&
        action.userDoc.account.freeWorkoutLimit
      ) {
        freeWorkoutLimit = action.userDoc.account.freeWorkoutLimit;
      }
      const accessCode = action?.userDoc?.entitlements?.allowPremium?.code;
      const freeWorkoutsLeft = getFreeWorkoutsLeft(
        state.freeWorkoutCount,
        freeWorkoutLimit
      );
      const updatedState = updateSubscriptionState(
        action.userDoc?.currentAccountStatus,
        state
      );
      return {
        ...state,
        ...updatedState,
        freeWorkoutLimit,
        freeWorkoutsLeft,
        accessCode,
      };
    }
    case TypeKeys.SET_ROUTE_DETAIL: {
      const url = action.routeDetail.to;
      const isFreeWorkout = url.includes('/kids-workouts/');
      const freeWorkoutsLeft = getFreeWorkoutsLeft(
        state.freeWorkoutCount,
        state.freeWorkoutLimit
      );
      return {
        ...state,
        isFreeWorkout,
        shouldShowPaywall: shouldShowPaywall(
          state.isAuthenticated,
          state.hasAccess,
          isFreeWorkout,
          freeWorkoutsLeft
        ),
        canPlayWorkout: canPlayWorkout(
          state.isAuthenticated,
          state.hasAccess,
          freeWorkoutsLeft,
          isFreeWorkout
        ),
      };
    }
    case TypeKeys.SIGN_OUT: {
      return { ...getInitialState() };
    }
  }
  return state;
};

export default subscription;

///////

function getFreeWorkoutsLeft(
  freeWorkoutCount: number,
  freeWorkoutLimit: number
) {
  const freeLimit = freeWorkoutLimit > 1 ? freeWorkoutLimit : 1;
  // give everyone at least 1 free workout.
  const freeWorkoutsLeft = freeLimit - freeWorkoutCount;
  return freeWorkoutsLeft > 0 ? freeWorkoutsLeft : 0;
}

function shouldShowPaywall(
  isAuthenticated: boolean,
  hasAccess: boolean,
  isFreeWorkout: boolean,
  freeWorkoutsLeft
) {
  if (!isAuthenticated) {
    return false;
  }
  if (isFreeWorkout || hasAccess || freeWorkoutsLeft > 1) {
    return false;
  } else {
    return true;
  }
}

function canPlayWorkout(
  isAuthenticated: boolean,
  hasAccess: boolean,
  freeWorkoutsLeft: number,
  isFreeWorkout: boolean
) {
  if (!isAuthenticated) {
    return true;
  }
  if (isFreeWorkout || hasAccess) {
    return true;
  } else {
    return freeWorkoutsLeft > 0;
  }
}

function getAccess(status: SubscriptionStatus) {
  switch (status) {
    case 'forever':
      return true;
    case 'active':
      return true;
    case 'expiring':
      return true;
    case 'expired':
      return false;
    case 'none':
      return false;
  }
}

export function getSubscriptionDescription(
  expires: number | string,
  status: SubscriptionStatus,
  source: SubscriptionResponse['source']
) {
  let formattedDate = expires;
  if (typeof expires === 'number') {
    formattedDate = formatDate(new Date(expires), 'MMMM do, yyyy');
  }

  switch (status) {
    case 'active':
      if (exemptFromExpiring.includes(source)) {
        return '';
      }
      return source === ('organization' as any) || source === 'sworkit-youth'
        ? translate('SUBSCRIPTION_ACTIVE_ORG')
        : translate('SUBSCRIPTION_AUTO_RENEW_ON', {
            date: formattedDate,
          });
    case 'expiring':
      if (exemptFromExpiring.includes(source)) {
        return '';
      }
      return source === ('organization' as any) || source === 'sworkit-youth'
        ? translate('SUBSCRIPTION_EXPIRING_ORG', {
            date: formattedDate,
          })
        : translate('SUBSCRIPTION_WILL_EXPIRE_ON', {
            date: formattedDate,
          });
    case 'expired':
      return translate('SUBSCRIPTION_EXPIRED_ON', {
        date: formattedDate,
      });
    case 'none':
      return '';
    case 'forever':
      return translate('FOREVER_SUBSCRIPTION_DESC');
  }
}

export function updateSubscriptionState(
  subscriptionResponse: SubscriptionResponse,
  state: SubscriptionState
): SubscriptionState {
  if (subscriptionResponse && subscriptionResponse.subscription_status) {
    const hasAccess = getAccess(subscriptionResponse.subscription_status);
    const expiration = subscriptionResponse.expires_date
      ? formatDate(new Date(subscriptionResponse.expires_date), 'MMMM do, yyyy')
      : null;

    const showExpiringSoon =
      subscriptionResponse.subscription_status === 'expiring' &&
      !exemptFromExpiring.includes(subscriptionResponse.source)
        ? subscriptionResponse.expires_date - 30 * 24 * 60 * 60 * 1000 <
          new Date().getTime()
        : false;
    const freeWorkoutsLeft = getFreeWorkoutsLeft(
      state.freeWorkoutCount,
      state.freeWorkoutLimit
    );
    return {
      ...state,
      hasAccess,
      label: translate(subscriptionResponse.subscription_translation_id),
      description: getSubscriptionDescription(
        subscriptionResponse.expires_date,
        subscriptionResponse.subscription_status,
        subscriptionResponse.source
      ),
      sourceTranslation: subscriptionResponse.display_source
        ? translate('SUBSCRIPTION_SOURCE', {
            source: subscriptionResponse.display_source,
          })
        : '',
      status: subscriptionResponse.subscription_status,
      subscriptionLoaded: true,
      expiration,
      source: subscriptionResponse.source,
      shouldShowPaywall: shouldShowPaywall(
        state.isAuthenticated,
        hasAccess,
        state.isFreeWorkout,
        freeWorkoutsLeft
      ),
      canPlayWorkout: canPlayWorkout(
        state.isAuthenticated,
        hasAccess,
        freeWorkoutsLeft,
        state.isFreeWorkout
      ),
      showExpiringSoon,
    };
  }
  return null;
}
